/* import __COLOCATED_TEMPLATE__ from './sq-loading.hbs'; */
/**
`sq-loading` is a loading spinner shown in the center of a component, while that component
is hidden and loading.

```example
@title Basic Example
@controller-properties
{
  isLoading: true
}

@hbs
<div style="position:relative; height: 100px; width: 100%;">
  {{sq-loading isActive=isLoading}}
</div>
```

```example
@title Small Spinner Example
@controller-properties
{
  isLoading: true
}

@hbs
<div style="position:relative; height: 100px; width: 100%;">
  {{sq-loading useSmallSpinner=true
               isActive=isLoading}}
</div>
```
## How to use in a component
The majority of uses of `sq-loading` involve waiting for the model to load, so the component
will be used like this:

```hbs
{{sq-loading isActive=model.isLoading}}

{{#if model.isLoaded}}
  // section of page which requires model to be loaded
{{/if}}
```

### Parameters
@param {Boolean} [useSmallSpinner=false] Set to true to use the smaller spinner size.
@param {Boolean} [isActive=false] Set to `true` to show `sq-loading`, false to hide. Should be `true` when the component calling `sq-loading` hasn't loaded.
@param {Boolean} [useMarketActivityIndicator=undefined] Automatically set to `true` when the `market-activity-indicator` web component is available, in which case it will render it instead of the Glass spinner. Set to `false` to always opt out of this behavior and always display the Glass spinner.
 */

import { schedule, cancel } from '@ember/runloop';
import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['loading'],
  classNameBindings: ['isActive:loading-is-active', 'useSmallSpinner:loading--small', 'useFixedPositioning:loading--fixed', 'useFixedPositioning:ecosystem-header-top-element', 'useMarketActivityIndicator:loading--use-market'],
  useFixedPositioning: false,
  useSmallSpinner: false,
  useMarketActivityIndicator: undefined,
  init() {
    this._super();
    // If the `market-activity-indicator` custom element is defined, we use it
    // instead of the Glass spinner.
    if (this.useMarketActivityIndicator !== false && customElements.get('market-activity-indicator')) {
      this.useMarketActivityIndicator = true;
    }
  },
  // Schedule new run loop instead of setting `useFixedPositioning` here, as per:
  // http://emberjs.com/deprecations/v1.x/#toc_modifying-a-property-within-code-didinsertelement-code
  didInsertElement(...args) {
    this._super(...args);
    this.checkOffsetParentTask = schedule('afterRender', this, this.checkOffsetParent);
  },
  willDestroyElement(...args) {
    this._super(...args);
    cancel(this.checkOffsetParentTask);
  },
  // If the element has a positioned offset parent (the element CSS uses
  // for absolute positioning) that's not `.l-viewport-app` or the top level
  // `<html>` element, use absolute positioning so the spinner can center
  // itself based on its context. Otherwise use fixed positioning to ensure
  // the spinner is centered vertically in the viewport even if the content has
  // no height.
  checkOffsetParent() {
    const offsetParent = this.get('element').offsetParent;
    const useFixedPositioning = !offsetParent || offsetParent.matches('.l-viewport-app, html');
    this.set('useFixedPositioning', useFixedPositioning);
  }
});